import { useState, useEffect, useCallback, useRef } from "react";
import { mergeLines } from "../lib/invoicePrint";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { cn, formatCurrency, formatDate, getStatusColor, API_URL } from "../lib/utils";
import { processedByText } from "../lib/fiscal";
import { TPASelector, useTPAs } from "../components/payments/TPASelector";
import { MixedPaymentEditor, emptyMixedParts, validateMixedParts, mixedPayload } from "../components/payments/MixedPaymentEditor";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Users,
  Clock,
  MoreVertical,
  Trash2,
  Edit,
  QrCode,
  Receipt,
  CreditCard,
  Banknote,
  ShoppingBag,
  ChefHat,
  Check,
  FileText,
  Send,
  Utensils,
  X,
  Minus,
  Bell,
  User,
  Printer,
  Download,
  Smartphone,
  Coins,
  Building2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { openHtmlDocument } from "../lib/docViewer";

const TablesPage = () => {
  const { t } = useLanguage();
  const { token, isManager } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [tableModal, setTableModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableDetailsModal, setTableDetailsModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [paymentModal, setPaymentModal] = useState(false);
  const [addItemsModal, setAddItemsModal] = useState(false);
  
  // Forms
  const [tableForm, setTableForm] = useState({
    number: "",
    name: "",
    capacity: 4,
    shape: "square",
    waiter_id: "",
    waiter_name: "",
  });

  // Add items to order
  const [cartItems, setCartItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  // Invoice print ref
  const invoiceRef = useRef(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const tpas = useTPAs(headers);
  const [tpaId, setTpaId] = useState("");
  const [mixedParts, setMixedParts] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [tableRes, orderRes, prodRes, catRes, empRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/tables`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/orders`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/categories`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/employees`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/settings`, { headers, withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setTables(tableRes.data);
      setOrders(orderRes.data);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setEmployees(empRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Get orders for a specific table
  const getTableOrders = (tableId) => {
    return orders.filter(
      (o) => o.table_id === tableId && !["completed", "cancelled"].includes(o.status)
    );
  };

  // Check if table has ready orders
  const hasReadyOrders = (tableId) => {
    return orders.some(
      (o) => o.table_id === tableId && o.status === "ready"
    );
  };

  // Get all ready orders count (for global notification)
  const totalReadyOrders = orders.filter(o => o.status === "ready").length;

  // Calculate table total
  const getTableTotal = (tableId) => {
    const tableOrders = getTableOrders(tableId);
    return tableOrders.reduce((sum, order) => sum + order.total, 0);
  };

  // Open table details
  const handleTableClick = (table) => {
    setSelectedTable(table);
    setTableDetailsModal(true);
  };

  // Create/Edit table
  const handleTableSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTable) {
        await axios.put(
          `${API_URL}/tables/${editingTable.table_id}`,
          tableForm,
          { headers, withCredentials: true }
        );
        toast.success("Mesa atualizada!");
      } else {
        await axios.post(`${API_URL}/tables`, tableForm, {
          headers,
          withCredentials: true,
        });
        toast.success("Mesa criada!");
      }
      setTableModal(false);
      setEditingTable(null);
      setTableForm({ number: "", name: "", capacity: 4, shape: "square", waiter_id: "", waiter_name: "" });
      fetchData();
    } catch (error) {
      toast.error("Erro ao guardar mesa");
    }
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableForm({
      number: table.number,
      name: table.name,
      capacity: table.capacity,
      shape: table.shape,
      waiter_id: table.waiter_id || "",
      waiter_name: table.waiter_name || "",
    });
    setTableModal(true);
  };

  // Get waiter name from employees
  const getWaiterName = (waiterId) => {
    if (!waiterId) return null;
    const waiter = employees.find(e => e.user_id === waiterId);
    return waiter?.name || null;
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm("Eliminar esta mesa?")) return;
    try {
      await axios.delete(`${API_URL}/tables/${tableId}`, {
        headers,
        withCredentials: true,
      });
      toast.success("Mesa eliminada!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar mesa");
    }
  };

  const handleStatusChange = async (tableId, status) => {
    try {
      await axios.put(
        `${API_URL}/tables/${tableId}/status?status=${status}`,
        {},
        { headers, withCredentials: true }
      );
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar estado");
    }
  };

  // Add items to existing order
  const handleAddItems = async () => {
    if (cartItems.length === 0) {
      toast.error("Adicione itens");
      return;
    }

    const tableOrders = getTableOrders(selectedTable.table_id);
    
    try {
      if (tableOrders.length > 0) {
        // Add to existing order
        await axios.put(
          `${API_URL}/orders/${tableOrders[0].order_id}/items`,
          cartItems.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            notes: item.notes,
          })),
          { headers, withCredentials: true }
        );
        toast.success("Itens adicionados ao pedido!");
      } else {
        // Create new order
        await axios.post(
          `${API_URL}/orders`,
          {
            table_id: selectedTable.table_id,
            order_type: "dine_in",
            items: cartItems.map((item) => ({
              product_id: item.product_id,
              quantity: item.quantity,
              notes: item.notes,
            })),
          },
          { headers, withCredentials: true }
        );
        toast.success("Novo pedido criado e enviado para cozinha!");
      }
      
      setCartItems([]);
      setAddItemsModal(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao adicionar itens");
    }
  };

  // Close table / Payment
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(false);
  
  const handlePayment = async (method) => {
    const tableOrders = getTableOrders(selectedTable.table_id);
    if (method === "card" && tpas.length && !tpaId) return toast.error("Selecione o TPA onde o cartão foi passado");
    const unpaid = tableOrders.filter((o) => o.payment_status !== "paid");
    if (method === "mixed") {
      if (unpaid.length > 1) return toast.error("Junte os pedidos da mesa num só antes de usar pagamento misto");
      const err = validateMixedParts(mixedParts || [], unpaid[0]?.total || 0, tpas);
      if (err) return toast.error(err);
    }
    
    try {
      let lastInvoice = null;
      
      // Pay all orders for this table - fatura é gerada automaticamente
      for (const order of tableOrders) {
        if (order.payment_status !== "paid") {
          const response = await axios.put(
            `${API_URL}/orders/${order.order_id}/pay?payment_method=${method}${method === "card" && tpaId ? `&tpa_id=${encodeURIComponent(tpaId)}` : ""}`,
            method === "mixed" ? mixedPayload(mixedParts) : {},
            { headers, withCredentials: true }
          );
          // Guardar a última fatura gerada
          if (response.data.invoice) {
            lastInvoice = response.data.invoice;
          }
        }
      }

      toast.success(
        <div className="flex items-center gap-2">
          <Check className="w-5 h-5" />
          <span>Conta fechada! Fatura gerada automaticamente.</span>
        </div>
      );
      
      setPaymentModal(false);
      setMixedParts(null);
      setTableDetailsModal(false);
      fetchData();
      
      // Mostrar fatura gerada
      if (lastInvoice) {
        setGeneratedInvoice(lastInvoice);
        setInvoiceModal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fechar conta");
    }
  };

  const addToCart = (product) => {
    setCartItems((prev) => {
      const existing = prev.find((item) => item.product_id === product.product_id);
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1, notes: "" }];
    });
  };

  const updateCartQuantity = (productId, delta) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.product_id === productId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const getQRUrl = (tableId) => {
    return `${window.location.origin}/menu/${tableId}`;
  };

  const copyQRUrl = (tableId) => {
    navigator.clipboard.writeText(getQRUrl(tableId));
    toast.success("URL copiado!");
  };

  const statusOptions = [
    { value: "free", label: "Livre", color: "bg-emerald-500" },
    { value: "occupied", label: "Ocupada", color: "bg-red-500" },
    { value: "reserved", label: "Reservada", color: "bg-amber-500" },
    { value: "cleaning", label: "Limpeza", color: "bg-blue-500" },
  ];

  const filteredProducts = products.filter((p) => {
    if (selectedCategory === "all") return p.is_available;
    return p.category_id === selectedCategory && p.is_available;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tables-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("tables")}</h1>
          <p className="text-muted-foreground">Clique numa mesa para ver detalhes e gerir pedidos</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/pos")}
            className="gap-2"
          >
            <ShoppingBag className="w-4 h-4" />
            Novo Pedido
          </Button>
          {isManager && (
            <Button
              onClick={() => {
                setEditingTable(null);
                setTableForm({ number: "", name: "", capacity: 4, shape: "square" });
                setTableModal(true);
              }}
              className="gap-2"
              data-testid="add-table-btn"
            >
              <Plus className="w-4 h-4" />
              Nova Mesa
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statusOptions.map((status) => {
          const count = tables.filter((t) => t.status === status.value).length;
          return (
            <Card key={status.value}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-sm text-muted-foreground">{status.label}</p>
                </div>
                <div className={`w-4 h-4 rounded-full ${status.color}`} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Ready Orders Alert */}
      {totalReadyOrders > 0 && (
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-full">
              <Bell className="w-5 h-5 text-white animate-bounce" />
            </div>
            <div>
              <p className="font-semibold text-blue-500">
                {totalReadyOrders} pedido{totalReadyOrders > 1 ? 's' : ''} pronto{totalReadyOrders > 1 ? 's' : ''} para servir!
              </p>
              <p className="text-sm text-muted-foreground">Clique na mesa para ver detalhes</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
            onClick={() => navigate("/kitchen")}
          >
            <ChefHat className="w-4 h-4 mr-2" />
            Ver Cozinha
          </Button>
        </div>
      )}

      {/* Tables Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {tables.map((table) => {
          const tableOrders = getTableOrders(table.table_id);
          const tableTotal = getTableTotal(table.table_id);
          const hasOrders = tableOrders.length > 0;
          const readyToServe = hasReadyOrders(table.table_id);
          const status = statusOptions.find((s) => s.value === table.status);

          return (
            <Card
              key={table.table_id}
              className={cn(
                "relative overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]",
                hasOrders && "ring-2 ring-primary",
                readyToServe && "ring-2 ring-blue-500 animate-pulse"
              )}
              onClick={() => handleTableClick(table)}
              data-testid={`table-${table.table_id}`}
            >
              {/* Ready to Serve Indicator */}
              {readyToServe && (
                <div className="absolute top-2 right-2 z-10">
                  <div className="p-1.5 bg-blue-500 rounded-full animate-bounce">
                    <Bell className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
              
              {/* Status Bar */}
              <div className={cn("h-2", readyToServe ? "bg-blue-500" : status?.color)} />

              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-xl">Mesa {table.number}</h3>
                    <p className="text-sm text-muted-foreground">{table.name}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {statusOptions.map((s) => (
                        <DropdownMenuItem
                          key={s.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(table.table_id, s.value);
                          }}
                          className={table.status === s.value ? "bg-muted" : ""}
                        >
                          <div className={`w-2 h-2 rounded-full mr-2 ${s.color}`} />
                          {s.label}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          copyQRUrl(table.table_id);
                        }}
                      >
                        <QrCode className="w-4 h-4 mr-2" />
                        Copiar QR URL
                      </DropdownMenuItem>
                      {isManager && (
                        <>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTable(table);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTable(table.table_id);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {table.capacity}
                  </span>
                  {table.occupied_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {Math.round((Date.now() - new Date(table.occupied_at)) / 60000)} min
                    </span>
                  )}
                </div>

                {/* Waiter Info */}
                {(table.waiter_name || table.waiter_id) && (
                  <div className="flex items-center gap-2 text-sm mb-3 p-2 rounded bg-muted/50">
                    <User className="w-4 h-4 text-primary" />
                    <span className="font-medium">
                      {table.waiter_name || getWaiterName(table.waiter_id) || "Garçom"}
                    </span>
                  </div>
                )}

                {/* Orders Info */}
                {hasOrders ? (
                  <div className="p-2 rounded-lg bg-primary/10 border border-primary/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{tableOrders.length} pedido(s)</span>
                      <span className="font-bold font-mono text-primary">
                        {formatCurrency(tableTotal)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <Badge className={getStatusColor(table.status)}>
                    {status?.label}
                  </Badge>
                )}
              </CardContent>
            </Card>
          );
        })}

        {tables.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Users className="w-12 h-12 mb-4 opacity-50" />
            <p>Nenhuma mesa criada</p>
          </div>
        )}
      </div>

      {/* Create/Edit Table Modal */}
      <Dialog open={tableModal} onOpenChange={setTableModal}>
        <DialogContent data-testid="table-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingTable ? "Editar Mesa" : "Nova Mesa"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleTableSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Número</Label>
                <Input
                  type="number"
                  value={tableForm.number}
                  onChange={(e) => setTableForm({ ...tableForm, number: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Capacidade</Label>
                <Input
                  type="number"
                  value={tableForm.capacity}
                  onChange={(e) => setTableForm({ ...tableForm, capacity: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={tableForm.name}
                onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
                placeholder="Ex: Mesa do Jardim"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Garçom Responsável
              </Label>
              <Select
                value={tableForm.waiter_id || "none"}
                onValueChange={(value) => {
                  const actualValue = value === "none" ? "" : value;
                  const waiter = employees.find(e => e.user_id === actualValue);
                  setTableForm({ 
                    ...tableForm, 
                    waiter_id: actualValue,
                    waiter_name: waiter?.name || ""
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar garçom..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {employees
                    .filter(e => e.role === "waiter" || e.role === "staff" || e.role === "admin" || e.role === "manager")
                    .map((emp) => (
                      <SelectItem key={emp.user_id} value={emp.user_id}>
                        {emp.name} ({emp.role === "waiter" ? "Garçom" : emp.role})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setTableModal(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Table Details Modal */}
      <Dialog open={tableDetailsModal} onOpenChange={setTableDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="table-details-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <Users className="w-5 h-5" />
              Mesa {selectedTable?.number} - {selectedTable?.name}
            </DialogTitle>
            <DialogDescription>
              Gerir pedidos e fechar conta
            </DialogDescription>
          </DialogHeader>

          {selectedTable && (
            <div className="space-y-6">
              {/* Table Status */}
              <div className="flex items-center gap-4">
                <Badge className={cn(
                  "text-sm px-3 py-1",
                  getStatusColor(selectedTable.status)
                )}>
                  {statusOptions.find((s) => s.value === selectedTable.status)?.label}
                </Badge>
                {selectedTable.occupied_at && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Ocupada há {Math.round((Date.now() - new Date(selectedTable.occupied_at)) / 60000)} min
                  </span>
                )}
              </div>

              {/* Orders List */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Pedidos da Mesa
                </h4>
                
                {getTableOrders(selectedTable.table_id).length > 0 ? (
                  <div className="space-y-3">
                    {getTableOrders(selectedTable.table_id).map((order) => (
                      <Card key={order.order_id} className={cn(
                        "overflow-hidden",
                        order.status === "ready" && "ring-2 ring-blue-500"
                      )}>
                        {order.status === "ready" && (
                          <div className="h-1 bg-blue-500 animate-pulse" />
                        )}
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold">#{order.order_number}</span>
                              <Badge className={cn(
                                order.status === "open" && "bg-emerald-500/20 text-emerald-500",
                                order.status === "preparing" && "bg-amber-500/20 text-amber-500",
                                order.status === "ready" && "bg-blue-500/20 text-blue-500 animate-pulse",
                                order.status === "served" && "bg-slate-500/20 text-slate-500"
                              )}>
                                {order.status === "open" && "Na Cozinha"}
                                {order.status === "preparing" && "Preparando"}
                                {order.status === "ready" && (
                                  <span className="flex items-center gap-1">
                                    <Bell className="w-3 h-3" />
                                    Pronto para Servir!
                                  </span>
                                )}
                                {order.status === "served" && "Servido"}
                              </Badge>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(order.created_at)}
                            </span>
                          </div>

                          {/* Ready to Serve Alert */}
                          {order.status === "ready" && (
                            <div className="mb-3 p-2 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Bell className="w-5 h-5 text-blue-500 animate-bounce" />
                                <span className="text-sm font-medium text-blue-500">Pronto para levantar na cozinha!</span>
                              </div>
                              <Button
                                size="sm"
                                variant="default"
                                className="bg-blue-500 hover:bg-blue-600"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await axios.put(
                                      `${API_URL}/orders/${order.order_id}/status?status=served`,
                                      {},
                                      { headers, withCredentials: true }
                                    );
                                    toast.success("Pedido marcado como servido!");
                                    fetchData();
                                  } catch (error) {
                                    toast.error("Erro ao atualizar");
                                  }
                                }}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Servido
                              </Button>
                            </div>
                          )}

                          <div className="space-y-2">
                            {mergeLines(order.items, { byNotes: true }).map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>
                                  <span className="font-medium">{item.quantity}x</span> {item.product_name}
                                  {item.notes && (
                                    <span className="text-muted-foreground ml-2">({item.notes})</span>
                                  )}
                                </span>
                                <span className="font-mono">
                                  {formatCurrency(item.quantity * item.unit_price)}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="flex justify-between items-center mt-3 pt-3 border-t">
                            <span className="font-semibold">Total do Pedido</span>
                            <span className="font-bold font-mono text-primary">
                              {formatCurrency(order.total)}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {/* Total */}
                    <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold">Total da Mesa</span>
                        <span className="text-2xl font-bold font-mono text-primary">
                          {formatCurrency(getTableTotal(selectedTable.table_id))}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Utensils className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhum pedido ativo nesta mesa</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCartItems([]);
                    setSelectedCategory("all");
                    setAddItemsModal(true);
                  }}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar Itens
                </Button>
                
                {getTableOrders(selectedTable.table_id).length > 0 && (
                  <Button
                    onClick={() => setPaymentModal(true)}
                    className="gap-2"
                    data-testid="close-account-btn"
                  >
                    <CreditCard className="w-4 h-4" />
                    Fechar Conta
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Items Modal */}
      <Dialog open={addItemsModal} onOpenChange={setAddItemsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="add-items-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              Adicionar Itens - Mesa {selectedTable?.number}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh]">
            {/* Products */}
            <div className="space-y-3">
              <ScrollArea className="w-full whitespace-nowrap">
                <div className="flex gap-2 pb-2">
                  <Button
                    variant={selectedCategory === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory("all")}
                  >
                    Todos
                  </Button>
                  {categories.map((cat) => (
                    <Button
                      key={cat.category_id}
                      variant={selectedCategory === cat.category_id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCategory(cat.category_id)}
                    >
                      {cat.name}
                    </Button>
                  ))}
                </div>
              </ScrollArea>

              <ScrollArea className="h-[400px]">
                <div className="grid grid-cols-2 gap-2 pr-4">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => addToCart(product)}
                      className="p-3 rounded-lg border bg-card text-left hover:border-primary transition-all"
                    >
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="font-mono text-primary">{formatCurrency(product.price)}</p>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Cart */}
            <div className="border-l pl-4">
              <h4 className="font-semibold mb-3">Itens a Adicionar</h4>
              
              {cartItems.length > 0 ? (
                <div className="space-y-2">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex items-center justify-between p-2 rounded bg-muted">
                      <div>
                        <p className="font-medium text-sm">{item.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {formatCurrency(item.price * item.quantity)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateCartQuantity(item.product_id, -1)}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateCartQuantity(item.product_id, 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <div className="pt-3 border-t">
                    <div className="flex justify-between font-semibold">
                      <span>Total</span>
                      <span className="font-mono text-primary">
                        {formatCurrency(cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0) * 1.23)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Selecione produtos para adicionar
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddItemsModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddItems} disabled={cartItems.length === 0} className="gap-2">
              <Send className="w-4 h-4" />
              Enviar para Cozinha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={paymentModal} onOpenChange={(o) => { setPaymentModal(o); if (!o) setMixedParts(null); }}>
        <DialogContent data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Fechar Conta - Mesa {selectedTable?.number}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted">
              <div className="flex justify-between items-center">
                <span className="text-lg">Total a Pagar</span>
                <span className="text-3xl font-bold font-mono text-primary">
                  {formatCurrency(getTableTotal(selectedTable?.table_id))}
                </span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Selecione a forma de pagamento. Uma fatura-recibo será emitida automaticamente.
            </p>

            <TPASelector tpas={tpas} value={tpaId} onChange={setTpaId} hint="Necessário apenas para pagamento com Cartão" />

            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => handlePayment("cash")}
                data-testid="pay-cash"
              >
                <Banknote className="w-8 h-8 text-emerald-500" />
                <span>Dinheiro</span>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => handlePayment("card")}
                data-testid="pay-card"
              >
                <CreditCard className="w-8 h-8 text-blue-500" />
                <span>Cartão</span>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => handlePayment("transfer")}
                data-testid="pay-transfer"
              >
                <Building2 className="w-8 h-8 text-purple-500" />
                <span>Transferência</span>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => handlePayment("mobile")}
                data-testid="pay-mobile"
              >
                <Smartphone className="w-8 h-8 text-orange-500" />
                <span>Multicaixa</span>
              </Button>
              <Button
                variant={mixedParts ? "default" : "outline"}
                size="lg"
                className="h-14 flex-row gap-2 col-span-2"
                onClick={() => setMixedParts(mixedParts ? null : emptyMixedParts(getTableOrders(selectedTable?.table_id || "").filter((o) => o.payment_status !== "paid").reduce((s, o) => s + (o.total || 0), 0), tpas))}
                data-testid="pay-mixed"
              >
                <Coins className="w-6 h-6 text-amber-500" />
                <span>Pagamento Misto (Numerário + TPA + …)</span>
              </Button>
            </div>
            {mixedParts && (
              <div className="space-y-2">
                <MixedPaymentEditor parts={mixedParts} onChange={setMixedParts} total={getTableOrders(selectedTable?.table_id || "").filter((o) => o.payment_status !== "paid").reduce((s, o) => s + (o.total || 0), 0)} tpas={tpas} />
                <Button className="w-full" onClick={() => handlePayment("mixed")} data-testid="pay-mixed-confirm">Confirmar pagamento misto & Faturar</Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPaymentModal(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={invoiceModal} onOpenChange={setInvoiceModal}>
        <DialogContent className="max-w-md" data-testid="invoice-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Fatura {generatedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>

          {/* Printable Invoice Content */}
          <div ref={invoiceRef} className="space-y-4 p-4 border rounded-lg bg-white text-black">
            {/* Header */}
            <div className="text-center border-b pb-4">
              <p className="text-lg font-bold">{settings?.restaurant_name || "Restaurante"}</p>
              <p className="text-xs text-gray-600">NIF: {settings?.nif || "000000000"}</p>
              <p className="text-xs text-gray-600">{settings?.address || "Angola"}</p>
              <p className="text-xs text-gray-600">Tel: {settings?.phone || ""}</p>
            </div>

            {/* Invoice Info */}
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600">Documento:</span>
                <span className="font-mono font-bold">{generatedInvoice?.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Data:</span>
                <span>{generatedInvoice?.created_at ? formatDate(generatedInvoice.created_at) : ""}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cliente:</span>
                <span>{generatedInvoice?.customer_name || "Consumidor Final"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">NIF:</span>
                <span>{generatedInvoice?.customer_nif || "999999999"}</span>
              </div>
            </div>

            {/* Items */}
            <div className="border-t border-b py-3 space-y-2">
              {mergeLines(generatedInvoice?.items).map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm gap-2">
                  <div>
                    <span className="font-medium">{item.product_name}</span>
                    <div className="text-xs text-gray-600">{item.quantity} {item.unit || "un."} × {formatCurrency(item.unit_price)}{item.tax_rate != null ? ` · IVA ${item.tax_rate}%` : ""}{item.discount_amount > 0 ? ` · desc. −${formatCurrency(item.discount_amount)}` : ""}</div>
                  </div>
                  <span className="font-mono">{formatCurrency(item.line_total ?? item.quantity * item.unit_price)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-mono">{formatCurrency(generatedInvoice?.subtotal || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>IVA ({generatedInvoice?.tax_rate || 14}%):</span>
                <span className="font-mono">{formatCurrency(generatedInvoice?.tax || 0)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>TOTAL:</span>
                <span className="font-mono">{formatCurrency(generatedInvoice?.total || 0)}</span>
              </div>
            </div>

            {/* Menção legal (Decreto 312/18) */}
            <div className="text-xs text-gray-500 pt-3 border-t space-y-1">
              <p className="text-center font-mono break-all">{generatedInvoice ? processedByText(generatedInvoice) : ""}</p>
              <p className="text-center">Documento processado por computador</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInvoiceModal(false)}>
              Fechar
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                const printContent = invoiceRef.current;
                if (!printContent) return;
                openHtmlDocument(`
                  <html>
                    <head>
                      <title>Fatura ${generatedInvoice?.invoice_number}</title>
                      <style>
                        body { font-family: 'Courier New', monospace; padding: 20px; max-width: 80mm; margin: 0 auto; }
                        .text-center { text-align: center; }
                        .font-bold { font-weight: bold; }
                        @media print { body { width: 80mm; } }
                      </style>
                    </head>
                    <body>${printContent.innerHTML}<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body>
                  </html>
                `, { title: `Fatura ${generatedInvoice?.invoice_number || ""}` });
              }} 
              className="gap-2"
            >
              <Printer className="w-4 h-4" />
              Imprimir
            </Button>
            <Button 
              onClick={() => {
                const printContent = invoiceRef.current;
                if (!printContent) return;
                openHtmlDocument(`
                  <html>
                    <head>
                      <title>Fatura ${generatedInvoice?.invoice_number}</title>
                      <style>
                        body { font-family: 'Courier New', monospace; padding: 20px; max-width: 80mm; margin: 0 auto; }
                        .text-center { text-align: center; }
                        .font-bold { font-weight: bold; }
                        @media print { body { width: 80mm; } }
                      </style>
                    </head>
                    <body>${printContent.innerHTML}<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body>
                  </html>
                `, { title: `Fatura ${generatedInvoice?.invoice_number || ""}` });
              }} 
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TablesPage;
